'use client';

import { fetchProductList, fetchRecommendProducts } from '@/api/product';
import { listByProductIds } from '@/api/public';
import { ListProduct } from '@/api/types/public';
import type { IProductListApiParams } from '@/api/types/product';
import InfiniteScroll from '@/components/InfiniteScroll';
import LoadingCircular from '@/components/LoadingCircular';
import GuessYouLike from '@/components/GuessYouLike';
import ProductCard, {
  type CorrelationHoverBehavior,
  type Product,
} from '@/components/Product/ProductCard';
import type { CorrelationIndicatorMotion } from '@/components/Product/Relate';
import YouMightLikeWaterFall from '@/app/cart/components/YouMightLikeWaterFall';
import type { RecommendProduct } from '@/app/cart/hooks/useCartSensorsTracking';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { EProductImageType } from '@/utils/enums/goods';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import type { MarketingRuntimeConfig, ProductRecommendConfig } from '../types';
import { ActivityType } from '../types';
import { useCapsuleAppCartRecommendReady } from '../pages/Capsule/components/CapsuleProductRecommend/useCapsuleAppCartRecommendReady';
import { useCampaignRecommendTracking } from '../pages/SeedingPage/hooks/useCampaignRecommendTracking';
import { useStore } from '@/providers/app-store-provider';
import type { HoverProductImagePriority } from '@/hooks/useProductImages';
import {
  clearCollaborationReturnState,
  getCollaborationReturnStorageKey,
  hasCollaborationReturnStateMarker,
  readCollaborationReturnState,
  saveCollaborationReturnState,
  scheduleCollaborationViewportRestore,
} from '@/utils/collaboration-product-return-state';

const DEFAULT_RECOMMEND_TITLE = 'You may also like';
const RECOMMEND_PRODUCT_LIST_KEYS = ['waterfall_list', 'frame_list', 'acc_list'] as const;

type CampaignRecommendParams = Parameters<typeof fetchRecommendProducts>[0];
type CampaignRecommendResponse = Awaited<ReturnType<typeof fetchRecommendProducts>>;
type CampaignRecommendListKey = (typeof RECOMMEND_PRODUCT_LIST_KEYS)[number];
type CampaignRecommendData = Omit<
  NonNullable<CampaignRecommendResponse['data']>,
  CampaignRecommendListKey
> &
  Partial<Record<CampaignRecommendListKey, RecommendProduct[]>>;
type CampaignCorrelationProduct = ListProduct & Pick<Product, 'correlation'>;

const GROUP_RECOMMEND_PAGE_CACHE_VERSION = 1;
const GROUP_RECOMMEND_PAGE_CACHE_TTL_MS = 30 * 60 * 1000;

interface GroupRecommendPageCache {
  version: typeof GROUP_RECOMMEND_PAGE_CACHE_VERSION;
  products: ListProduct[];
  page: number;
  hasMore: boolean;
  total: number;
  savedAt: number;
}

function isValidGroupRecommendPageCache(value: unknown): value is GroupRecommendPageCache {
  if (!value || typeof value !== 'object') return false;
  const cache = value as Partial<GroupRecommendPageCache>;
  return (
    cache.version === GROUP_RECOMMEND_PAGE_CACHE_VERSION &&
    Array.isArray(cache.products) &&
    typeof cache.page === 'number' &&
    typeof cache.hasMore === 'boolean' &&
    typeof cache.total === 'number' &&
    typeof cache.savedAt === 'number' &&
    Date.now() - cache.savedAt <= GROUP_RECOMMEND_PAGE_CACHE_TTL_MS
  );
}

function findCampaignRecommendReturnTarget(section: HTMLElement | null, targetKey: string) {
  return Array.from(
    section?.querySelectorAll<HTMLElement>('[data-campaign-recommend-return-target]') || [],
  ).find((element) => element.dataset.campaignRecommendReturnTarget === targetKey);
}

async function fetchWapGoodsDetailRecommendProducts(
  params: CampaignRecommendParams,
  productListRequestOptions?: ProductListRequestOptions,
): Promise<CampaignRecommendResponse> {
  const response = await fetchRecommendProducts(params);
  if (response.code !== 200 || !response.data) return response;

  // 生成接口把 waterfall_list 标成 string[]，实际返回与 frame_list 一致的商品对象数组。
  const recommendData = response.data as unknown as CampaignRecommendData;
  const productIds = Array.from(
    new Set(
      RECOMMEND_PRODUCT_LIST_KEYS.flatMap((key) => recommendData[key] || [])
        .map((item) => Number(item.id))
        .filter((id) => id > 0),
    ),
  );
  if (productIds.length === 0) return response;

  try {
    const productResponse = await fetchProductList({
      append_correlation: 1,
      limit: productIds.length,
      page: 1,
      product_ids: productIds,
      ...productListRequestOptions,
    });
    if (productResponse.code !== 200 || !Array.isArray(productResponse.data?.list)) {
      return response;
    }

    const productList = productResponse.data.list as CampaignCorrelationProduct[];
    const productMap = new Map(
      productList.filter((item) => Number(item.id) > 0).map((item) => [Number(item.id), item]),
    );
    RECOMMEND_PRODUCT_LIST_KEYS.forEach((key) => {
      const list = recommendData[key];
      if (!Array.isArray(list)) return;

      recommendData[key] = list.map((item) => {
        const product = productMap.get(Number(item.id));
        if (!product) return item;

        const isGlass = item.is_glass ?? product.is_glass;
        const normalizedIsGlass = isGlass === undefined ? undefined : Number(isGlass);

        return {
          ...item,
          correlation: product.correlation,
          is_glass: Number.isFinite(normalizedIsGlass) ? normalizedIsGlass : item.is_glass,
        };
      });
    });

    return response;
  } catch (error) {
    logger.error('补齐活动页推荐商品关联品失败:', error);
    return response;
  }
}

/** 与购物车底部 YouMightLikeWaterFall 保持一致的视觉样式，但不耦合组件实现 */
const RECOMMEND_SECTION_CLASS = {
  root: (isWap: boolean) =>
    `bg-white font-regular w-full ${isWap ? 'mx-auto pt-[24px] px-[8px]' : 'mx-auto pt-[40px] px-[48px]'} pb-[60px]`,
  title: (isWap: boolean) =>
    `font-bb text-center ${isWap ? 'text-[20px] leading-[24px] mb-[16px]' : 'text-[32px] leading-[40px] mb-[32px]'}`,
  grid: (isWap: boolean) =>
    isWap
      ? 'grid grid-cols-2 gap-x-[8px] gap-y-[40px] relative'
      : 'grid gap-x-[16px] gap-y-[24px] 2xl:grid-cols-4 xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 pb-[64px]',
  infiniteScroll: (isWap: boolean) =>
    isWap ? 'pt-[40px]' : 'h-[64px] flex items-center justify-center',
} as const;

interface ProductRecommendProps {
  config: ProductRecommendConfig;
  runtimeConfig: MarketingRuntimeConfig;
  enableAdTracking?: boolean;
  onProductCollect?: CampaignProductCollectTrackingHandler;
  onProductClick?: CampaignProductTrackingHandler;
  onProductExposure?: CampaignProductExposureHandler;
  onModelExposure?: () => void;
  /** 推荐商品全部加载完成（含无数据场景） */
  onLoadComplete?: () => void;
  /** 商品展示方式；默认保持活动页原网格，联名页使用商详推荐样式。 */
  presentation?: ProductRecommendPresentation;
  /** 可选的商品详情返回定位命名空间；仅需要恢复分页状态的页面传入。 */
  returnStateKey?: string;
  /** 商品 hover 图片类型优先级；未传时保持公共商品卡默认策略。 */
  imageHoverRule?: EProductImageType[];
  hoverProductImagePriority?: HoverProductImagePriority;
  /** 关联品 hover 策略；仅显式调用方生效。 */
  correlationHoverBehavior?: CorrelationHoverBehavior;
  /** 商品卡视觉参数；未传时保持公共推荐卡默认表现。 */
  productCardOptions?: ProductRecommendProductCardOptions;
  productListRequestOptions?: ProductListRequestOptions;
}

export interface ProductRecommendProductCardOptions {
  imageClassName?: string;
  productInfoClassName?: string;
  correlationIndicatorMotion?: CorrelationIndicatorMotion;
}

type ProductListRequestOptions = Pick<IProductListApiParams, 'in_stock' | 'stock_sort_first'>;

export type ProductRecommendPresentation = 'campaign-grid' | 'goods-detail';

export type CampaignProductTrackingHandler = (
  item: ListProduct | RecommendProduct,
  index: number,
  tabName: string,
) => void;

export type CampaignProductExposureHandler = CampaignProductTrackingHandler;

export type CampaignProductCollectTrackingHandler = (
  item: ListProduct | RecommendProduct,
  index: number,
  collectType: string,
) => void;

/** 是否配置了可展示的推荐商品版位 */
export function isProductRecommendEnabled(config?: ProductRecommendConfig): boolean {
  if (!config?.source_type) return false;
  if (config.source_type === 'algorithm') return true;
  if (config.source_type === 'group') return Number(config.group_id) > 0;
  return false;
}

/**
 * 商品推荐版位组件
 * 支持 source_type='group'（指定分组）和 source_type='algorithm'（算法推荐）两种模式
 */
export default function ProductRecommend({
  config,
  runtimeConfig,
  enableAdTracking = false,
  onProductCollect,
  onProductClick,
  onProductExposure,
  onModelExposure,
  onLoadComplete,
  presentation = 'campaign-grid',
  returnStateKey,
  imageHoverRule,
  hoverProductImagePriority,
  correlationHoverBehavior,
  productCardOptions,
  productListRequestOptions,
}: ProductRecommendProps) {
  const { deviceType } = useContext(ResponsiveContext);

  if (config.source_type === 'algorithm') {
    return (
      <AlgorithmRecommend
        runtimeConfig={runtimeConfig}
        deviceType={deviceType}
        algorithmValue={config.algorithm_value}
        title={config.section_title}
        enableAdTracking={enableAdTracking}
        onProductCollect={onProductCollect}
        onProductClick={onProductClick}
        onProductExposure={onProductExposure}
        onModelExposure={onModelExposure}
        onLoadComplete={onLoadComplete}
        presentation={presentation}
        returnStateKey={returnStateKey ? `${returnStateKey}:algorithm` : undefined}
        imageHoverRule={imageHoverRule}
        hoverProductImagePriority={hoverProductImagePriority}
        correlationHoverBehavior={correlationHoverBehavior}
        productCardOptions={productCardOptions}
        productListRequestOptions={productListRequestOptions}
      />
    );
  }

  if (config.source_type === 'group' && config.group_id) {
    return (
      <GroupRecommend
        runtimeConfig={runtimeConfig}
        groupId={config.group_id}
        deviceType={deviceType}
        title={config.section_title}
        enableAdTracking={enableAdTracking}
        onProductCollect={onProductCollect}
        onProductClick={onProductClick}
        onProductExposure={onProductExposure}
        onModelExposure={onModelExposure}
        onLoadComplete={onLoadComplete}
        presentation={presentation}
        returnStateKey={returnStateKey ? `${returnStateKey}:group:${config.group_id}` : undefined}
        imageHoverRule={imageHoverRule}
        hoverProductImagePriority={hoverProductImagePriority}
        correlationHoverBehavior={correlationHoverBehavior}
        productCardOptions={productCardOptions}
        productListRequestOptions={productListRequestOptions}
      />
    );
  }

  return null;
}

interface GroupRecommendProps {
  runtimeConfig: MarketingRuntimeConfig;
  groupId: number;
  deviceType: number;
  title?: string;
  enableAdTracking?: boolean;
  onProductCollect?: CampaignProductCollectTrackingHandler;
  onProductClick?: CampaignProductTrackingHandler;
  onProductExposure?: CampaignProductExposureHandler;
  onModelExposure?: () => void;
  onLoadComplete?: () => void;
  presentation: ProductRecommendPresentation;
  returnStateKey?: string;
  imageHoverRule?: EProductImageType[];
  hoverProductImagePriority?: HoverProductImagePriority;
  correlationHoverBehavior?: CorrelationHoverBehavior;
  productCardOptions?: ProductRecommendProductCardOptions;
  productListRequestOptions?: ProductListRequestOptions;
}

function GroupRecommend({
  runtimeConfig,
  groupId,
  deviceType,
  title,
  enableAdTracking = false,
  onProductCollect,
  onProductClick,
  onProductExposure,
  onModelExposure,
  onLoadComplete,
  presentation,
  returnStateKey,
  imageHoverRule,
  hoverProductImagePriority,
  correlationHoverBehavior,
  productCardOptions,
  productListRequestOptions,
}: GroupRecommendProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const swipePagination = !isWap && runtimeConfig.template_type === ActivityType.COLLABORATION;
  const sectionTitle = title || DEFAULT_RECOMMEND_TITLE;
  const getPageCache = useStore((state) => state.getPageCache);
  const setPageCache = useStore((state) => state.setPageCache);
  const clearPageCache = useStore((state) => state.clearPageCache);
  const returnStorageKey = returnStateKey
    ? getCollaborationReturnStorageKey(returnStateKey, 'product-recommend')
    : undefined;
  const pageCacheKey = returnStateKey
    ? `collaboration:page-cache:${returnStateKey}:product-recommend`
    : undefined;
  const cachedPage = pageCacheKey ? getPageCache(pageCacheKey) : null;
  const restoredPageCache = useRef(
    returnStorageKey &&
      hasCollaborationReturnStateMarker(returnStorageKey) &&
      isValidGroupRecommendPageCache(cachedPage)
      ? cachedPage
      : null,
  ).current;
  const [list, setList] = useState<ListProduct[]>(restoredPageCache?.products || []);
  const [page, setPage] = useState(restoredPageCache?.page || 0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(restoredPageCache?.hasMore ?? true);
  const [total, setTotal] = useState(restoredPageCache?.total || 0);
  const loadingRef = useRef(false);
  const latestRequestIdRef = useRef(0);
  const loadCompleteNotifiedRef = useRef(false);
  const modelExpSentRef = useRef(false);
  const prevTokenRef = useRef<string | undefined>(undefined);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const restoreCleanupRef = useRef<(() => void) | null>(null);
  const token = useStore((state) => state.token);

  const {
    trackRecommendClick,
    trackRecommendAdExposure,
    trackRecommendAdClick,
    trackRecommendCollectClick,
    trackRecommendValidExp,
    trackRecommendModelExp,
    collectScene,
  } = useCampaignRecommendTracking(runtimeConfig);
  const handleModelExposure = onModelExposure || trackRecommendModelExp;

  const { ref: titleRef, inView: titleInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const handleProductValidExposure = useCallback(
    (item: ListProduct, index: number) => {
      if (onProductExposure) {
        onProductExposure(item, index, sectionTitle);
      } else {
        trackRecommendValidExp(item, index);
      }

      if (enableAdTracking) {
        trackRecommendAdExposure(item, index);
      }
    },
    [
      enableAdTracking,
      onProductExposure,
      sectionTitle,
      trackRecommendAdExposure,
      trackRecommendValidExp,
    ],
  );

  const notifyLoadComplete = useCallback(() => {
    if (loadCompleteNotifiedRef.current) return;
    loadCompleteNotifiedRef.current = true;
    onLoadComplete?.();
  }, [onLoadComplete]);

  const loadPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (loadingRef.current) return;

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      const isLatestRequest = () => latestRequestIdRef.current === requestId;

      loadingRef.current = true;
      setLoading(true);

      try {
        const res = await fetchProductList({
          group_id: groupId,
          page: nextPage,
          limit: 20,
          append_correlation: 1,
          ...productListRequestOptions,
        });
        if (!isLatestRequest()) return;

        if (res.code === 200 && res.data) {
          const newList = res.data.list || [];
          setList((prev) => {
            const merged = append ? [...prev, ...newList] : newList;
            if (newList.length < 20 || (res.data?.total && merged.length >= res.data.total)) {
              setHasMore(false);
            } else {
              setHasMore(true);
            }
            return merged;
          });
          setPage(nextPage);
          setTotal(res.data.total || 0);
        } else {
          if (!append) {
            setList([]);
            setPage(0);
            setTotal(0);
          }
          setHasMore(false);
        }
      } catch (error) {
        if (isLatestRequest()) {
          logger.error(error, 'Failed to load group products:');
          if (!append) {
            setList([]);
            setPage(0);
            setTotal(0);
          }
          setHasMore(false);
        }
      } finally {
        if (isLatestRequest()) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [groupId, productListRequestOptions],
  );

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    await loadPage(page + 1, true);
  }, [hasMore, loadPage, page]);

  const updateProductsWishStatus = useCallback(async () => {
    try {
      if (list.length === 0) return;

      const productIds = list.map((item) => item.id || 0).filter((id) => id > 0);
      if (productIds.length === 0) return;

      const response = await listByProductIds({ product_ids: productIds.join(',') });
      if (response?.code !== 200 || !Array.isArray(response.data?.list)) return;

      const wishMap = response.data.list.reduce<Record<number, number>>((acc, item) => {
        if (item.id && typeof item.is_wish === 'number') {
          acc[item.id] = item.is_wish;
        }
        return acc;
      }, {});

      setList((prevList) =>
        prevList.map((item) => ({
          ...item,
          is_wish: item.id && wishMap[item.id] !== undefined ? wishMap[item.id] : item.is_wish,
        })),
      );
    } catch (error) {
      logger.error(error, 'Failed to update campaign recommend wish status:');
    }
  }, [list]);

  useWhenTokenReady(() => {
    if (prevTokenRef.current === undefined) {
      prevTokenRef.current = token;
      return;
    }

    const prevToken = prevTokenRef.current;

    if (prevToken && !token) {
      setList((prevList) =>
        prevList.map((item) => ({
          ...item,
          is_wish: 2,
        })),
      );
    }

    if (token && prevToken !== token) {
      void updateProductsWishStatus();
    }

    prevTokenRef.current = token;
  }, [token, updateProductsWishStatus]);

  useEffect(() => {
    if (!restoredPageCache) {
      void loadPage(1, false);
    }
  }, [loadPage, restoredPageCache]);

  useEffect(() => {
    if (!returnStorageKey || !pageCacheKey || list.length === 0) return;
    const returnState = readCollaborationReturnState(returnStorageKey);
    if (!returnState) return;

    const target = findCampaignRecommendReturnTarget(sectionRef.current, returnState.targetKey);
    if (!target) return;

    restoreCleanupRef.current?.();
    if (typeof returnState.viewportTop === 'number') {
      restoreCleanupRef.current = scheduleCollaborationViewportRestore(
        target,
        returnState.viewportTop,
      );
    } else {
      target.scrollIntoView({ block: 'center' });
    }
    clearCollaborationReturnState(returnStorageKey);
    clearPageCache(pageCacheKey);
  }, [clearPageCache, list.length, pageCacheKey, returnStorageKey]);

  useEffect(
    () => () => {
      restoreCleanupRef.current?.();
    },
    [],
  );

  useEffect(() => {
    if (!loading && !hasMore) {
      notifyLoadComplete();
    }
  }, [loading, hasMore, notifyLoadComplete]);

  useEffect(() => {
    if (titleInView && list.length > 0 && !modelExpSentRef.current) {
      modelExpSentRef.current = true;
      handleModelExposure();
    }
  }, [handleModelExposure, titleInView, list.length]);

  const saveReturnState = useCallback(
    (item: ListProduct) => {
      if (!returnStorageKey || !pageCacheKey) return;
      const targetKey = item.shop_sku || String(item.id || '');
      if (!targetKey) return;

      const target = findCampaignRecommendReturnTarget(sectionRef.current, targetKey);
      if (
        !saveCollaborationReturnState(
          returnStorageKey,
          targetKey,
          target?.getBoundingClientRect().top,
        )
      ) {
        clearPageCache(pageCacheKey);
        return;
      }
      setPageCache(pageCacheKey, {
        version: GROUP_RECOMMEND_PAGE_CACHE_VERSION,
        products: list,
        page,
        hasMore,
        total,
        savedAt: Date.now(),
      } satisfies GroupRecommendPageCache);
    },
    [clearPageCache, hasMore, list, page, pageCacheKey, returnStorageKey, setPageCache, total],
  );

  const renderProductCard = useCallback(
    (item: ListProduct, index: number, cardPresentation: ProductRecommendPresentation) => (
      <ProductCard
        product={{ ...item, name: item.name || item.short_name || item.shop_sku || '' }}
        index={index}
        showTryOn={false}
        handelClick={(currentIndex, currentProduct) => {
          const trackingItem = currentProduct as ListProduct;
          saveReturnState(item);
          if (onProductClick) {
            trackRecommendClick(trackingItem, currentIndex, (clickedItem, clickedIndex) => {
              onProductClick(clickedItem, clickedIndex, sectionTitle);
            });
          } else {
            trackRecommendClick(trackingItem, currentIndex);
          }
          if (enableAdTracking) {
            trackRecommendAdClick(trackingItem, currentIndex);
          }
        }}
        onValidExposure={(currentIndex, currentProduct) =>
          handleProductValidExposure(currentProduct as ListProduct, currentIndex)
        }
        wished={(currentProduct, currentIndex) => {
          const trackingItem = currentProduct as ListProduct;
          if (onProductCollect) {
            onProductCollect(trackingItem, currentIndex, '收藏');
          } else {
            trackRecommendCollectClick(trackingItem, currentIndex, '收藏');
          }
        }}
        removed={(currentProduct, currentIndex) => {
          const trackingItem = currentProduct as ListProduct;
          if (onProductCollect) {
            onProductCollect(trackingItem, currentIndex, '取消收藏');
          } else {
            trackRecommendCollectClick(trackingItem, currentIndex, '取消收藏');
          }
        }}
        collectScene={collectScene}
        className={
          cardPresentation === 'goods-detail'
            ? isWap
              ? 'mb-40'
              : ''
            : isWap
              ? 'bg-none'
              : 'h-full bg-none'
        }
        imageClassName={
          [
            isWap
              ? cardPresentation === 'goods-detail'
                ? 'rounded-12 aspect-square'
                : 'h-[176px] rounded-[12px]'
              : undefined,
            productCardOptions?.imageClassName,
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        productInfoClassName={
          [
            isWap && cardPresentation === 'goods-detail' ? 'pl-[3px] pr-[3px]' : undefined,
            productCardOptions?.productInfoClassName,
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        isSquare={isWap && cardPresentation === 'goods-detail'}
        frameWidth={cardPresentation === 'goods-detail' ? '70%' : undefined}
        showCorrelation
        showCorrelationCount={isWap ? 3 : 6}
        imageHoverRule={imageHoverRule}
        hoverProductImagePriority={hoverProductImagePriority}
        correlationHoverBehavior={correlationHoverBehavior}
        correlationIndicatorMotion={productCardOptions?.correlationIndicatorMotion}
      />
    ),
    [
      isWap,
      enableAdTracking,
      handleProductValidExposure,
      onProductCollect,
      onProductClick,
      sectionTitle,
      trackRecommendClick,
      trackRecommendAdClick,
      trackRecommendCollectClick,
      collectScene,
      saveReturnState,
      imageHoverRule,
      hoverProductImagePriority,
      correlationHoverBehavior,
      productCardOptions,
    ],
  );

  const renderGridItem = useCallback(
    (item: ListProduct, index: number) => (
      <div
        key={`${item.shop_sku || item.id}-${index}`}
        className="bg-none"
        data-campaign-recommend-return-target={item.shop_sku || String(item.id || '')}
      >
        {renderProductCard(item, index, 'campaign-grid')}
      </div>
    ),
    [renderProductCard],
  );

  const renderGoodsDetailItem = useCallback(
    (item: Product, index: number) => (
      <div
        className="block"
        data-campaign-recommend-return-target={item.shop_sku || String(item.id || '')}
      >
        {renderProductCard(item as ListProduct, index, 'goods-detail')}
      </div>
    ),
    [renderProductCard],
  );

  if (list.length === 0 && !loading) {
    return null;
  }

  if (presentation === 'goods-detail') {
    return (
      <div
        ref={(element) => {
          titleRef(element);
          sectionRef.current = element;
        }}
        className={isWap ? 'relative pb-20' : 'py-[60px]'}
      >
        <GuessYouLike
          title={sectionTitle}
          list={list as Product[]}
          canSwipe={!isWap}
          pagination={swipePagination ? { loading, hasMore, onLoadMore: loadMore } : undefined}
          divideCount={4}
          autoPlay={!isWap}
          showSkeleton
          skeletonCount={isWap ? 2 : 4}
          showCorrelation
          showCorrelationCount={isWap ? 3 : 6}
          renderTitle={() =>
            isWap ? (
              <>
                <div className="h-[10px] bg-auxiliary-gray" />
                <h3 className="text-20 font-bb text-primary-text pt-[34px] pb-16 text-center">
                  {sectionTitle}
                </h3>
              </>
            ) : (
              <h3 className="text-24 font-bb text-primary-text pb-[2vw] text-center">
                {sectionTitle}
              </h3>
            )
          }
          renderItem={renderGoodsDetailItem}
        />
        {hasMore &&
          (swipePagination ? (
            <div className={RECOMMEND_SECTION_CLASS.infiniteScroll(false)}>
              {loading && <LoadingCircular />}
            </div>
          ) : (
            <InfiniteScroll
              loading={loading}
              onReachBottom={loadMore}
              page={page}
              total={total}
              limit={20}
              className={RECOMMEND_SECTION_CLASS.infiniteScroll(isWap)}
            />
          ))}
      </div>
    );
  }

  return (
    <div ref={sectionRef} className={RECOMMEND_SECTION_CLASS.root(isWap)}>
      <div ref={titleRef} className={RECOMMEND_SECTION_CLASS.title(isWap)}>
        {sectionTitle}
      </div>

      <div className={RECOMMEND_SECTION_CLASS.grid(isWap)}>
        {list.map((item, index) => renderGridItem(item, index))}
      </div>

      {hasMore && (
        <InfiniteScroll
          loading={loading}
          onReachBottom={loadMore}
          page={page}
          total={total}
          limit={20}
          className={RECOMMEND_SECTION_CLASS.infiniteScroll(isWap)}
        />
      )}
    </div>
  );
}

interface AlgorithmRecommendProps {
  runtimeConfig: MarketingRuntimeConfig;
  deviceType: number;
  algorithmValue?: string;
  title?: string;
  enableAdTracking?: boolean;
  onProductCollect?: CampaignProductCollectTrackingHandler;
  onProductClick?: CampaignProductTrackingHandler;
  onProductExposure?: CampaignProductExposureHandler;
  onModelExposure?: () => void;
  onLoadComplete?: () => void;
  presentation: ProductRecommendPresentation;
  returnStateKey?: string;
  imageHoverRule?: EProductImageType[];
  hoverProductImagePriority?: HoverProductImagePriority;
  correlationHoverBehavior?: CorrelationHoverBehavior;
  productCardOptions?: ProductRecommendProductCardOptions;
  productListRequestOptions?: ProductListRequestOptions;
}

/** App 没有 Header，只在联名购物车算法分支补齐初始化；失败也允许推荐降级。 */
function CollaborationAppCartRecommend({
  deviceType,
  children,
}: {
  deviceType: EPlatform;
  children: ReactNode;
}) {
  const ready = useCapsuleAppCartRecommendReady(deviceType);
  return ready ? children : null;
}

function AlgorithmRecommend({
  runtimeConfig,
  deviceType,
  algorithmValue,
  title,
  enableAdTracking = false,
  onProductCollect,
  onProductClick,
  onProductExposure,
  onModelExposure,
  onLoadComplete,
  presentation,
  returnStateKey,
  imageHoverRule,
  hoverProductImagePriority,
  correlationHoverBehavior,
  productCardOptions,
  productListRequestOptions,
}: AlgorithmRecommendProps) {
  const sectionTitle = title || DEFAULT_RECOMMEND_TITLE;
  // 该场景由联名需求引入，其他模板保持需求前的推荐请求，不增加等待门禁。
  const isCartAlgorithm =
    runtimeConfig.template_type === ActivityType.COLLABORATION && algorithmValue === 'cart';
  const initializeAppCart =
    isCartAlgorithm && (deviceType === EPlatform.IOS || deviceType === EPlatform.ANDROID);
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const {
    trackRecommendClick,
    trackRecommendAdExposure,
    trackRecommendAdClick,
    trackRecommendCollectClick,
    trackRecommendValidExp,
    trackRecommendModelExp,
    collectScene,
  } = useCampaignRecommendTracking(runtimeConfig);

  const handleProductValidExposure = useCallback(
    (item: Parameters<typeof trackRecommendValidExp>[0], index: number) => {
      if (onProductExposure) {
        onProductExposure(item, index, sectionTitle);
      } else {
        trackRecommendValidExp(item, index);
      }

      if (enableAdTracking) {
        trackRecommendAdExposure(item, index);
      }
    },
    [
      enableAdTracking,
      onProductExposure,
      sectionTitle,
      trackRecommendAdExposure,
      trackRecommendValidExp,
    ],
  );

  const handleProductClick = useCallback(
    (item: Parameters<typeof trackRecommendClick>[0], index: number) => {
      if (onProductClick) {
        trackRecommendClick(item, index, (clickedItem, clickedIndex) => {
          onProductClick(clickedItem, clickedIndex, sectionTitle);
        });
      } else {
        trackRecommendClick(item, index);
      }
      if (enableAdTracking) {
        trackRecommendAdClick(item, index);
      }
    },
    [enableAdTracking, onProductClick, sectionTitle, trackRecommendAdClick, trackRecommendClick],
  );

  const handleProductCollect = useCallback(
    (
      item: Parameters<typeof trackRecommendCollectClick>[0],
      index: number,
      collectType?: string,
    ) => {
      if (onProductCollect) {
        onProductCollect(item, index, collectType || '');
      } else {
        trackRecommendCollectClick(item, index, collectType);
      }
    },
    [onProductCollect, trackRecommendCollectClick],
  );
  const goodsDetailRecommendRequest = useCallback(
    (params: CampaignRecommendParams) =>
      fetchWapGoodsDetailRecommendProducts(params, productListRequestOptions),
    [productListRequestOptions],
  );

  const recommendation = (
    <YouMightLikeWaterFall
      key={`campaign-recommend-${deviceType}-${token}`}
      recommendList={[]}
      deviceType={deviceType as EPlatform}
      sectionTitle={sectionTitle}
      onAllLoaded={onLoadComplete}
      tracking={{
        onItemClick: handleProductClick,
        // 旧普通曝光入口仅保留接口兼容，商品与广告曝光统一由 Valid handler 触发。
        onProductInView: () => undefined,
        onProductValidExposure: handleProductValidExposure,
        onCollectClick: handleProductCollect,
        onModelExp: onModelExposure || trackRecommendModelExp,
        collectScene,
      }}
      pcGridClassName={RECOMMEND_SECTION_CLASS.grid(false)}
      syncWishOnTokenChange
      waitForCartData={isCartAlgorithm && !initializeAppCart}
      scene={isCartAlgorithm ? 'cart' : undefined}
      presentation={presentation === 'goods-detail' ? 'goods-detail' : 'waterfall'}
      swipePagination={
        runtimeConfig.template_type === ActivityType.COLLABORATION && deviceType === EPlatform.WEB
      }
      recommendRequest={presentation === 'goods-detail' ? goodsDetailRecommendRequest : undefined}
      cacheKey={returnStateKey}
      returnStateKey={returnStateKey}
      imageHoverRule={imageHoverRule}
      hoverProductImagePriority={hoverProductImagePriority}
      correlationHoverBehavior={correlationHoverBehavior}
      productCardOptions={productCardOptions}
    />
  );
  if (initializeAppCart && !clientTokenReady) return null;
  return initializeAppCart ? (
    <CollaborationAppCartRecommend deviceType={deviceType}>
      {recommendation}
    </CollaborationAppCartRecommend>
  ) : (
    recommendation
  );
}
